import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import {
  buildTaxReportView,
  THRESHOLD_BY_YEAR,
  type TaxReportView,
} from './taxReportModel';
import { thresholdMapFromRows } from '../compliance/complianceModel';
import type {
  CashoutRequest,
  ComplianceThreshold,
  User,
} from '../dataProvider/types';

/**
 * The 1099-K tax report, read through the aggregation seam (`buildTaxReportView`), NOT computed in
 * the component. For v1 it pulls the cashout requests and users from the mock provider and derives
 * client-side; the real-data migration replaces the fetch-and-derive with a server-side endpoint
 * and the surface does not change. Same seam idiom as useSanctionsQueue / useDsarQueue.
 *
 * US-ONLY and PURE READ: the model fixes the scope to `market === 'us'` and writes nothing. The
 * `taxYear` keys the query so changing the year in the surface refetches the derived report.
 *
 * Since S4 (D-settings-s4-us-compliance) the reporting THRESHOLDS are editable config, so this hook
 * fetches the complianceThresholds rows as a THIRD input and passes the per-year map into the model
 * MERGED over the statutory THRESHOLD_BY_YEAR (config wins per year; a year the config omits falls
 * back to the constant, so an absent config still renders the statutory figure, never blank). This
 * is the LIVE connection: a threshold edited in the S4 surface flows through to the crossing gate.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useTaxReport = (
  taxYear: number,
): { view: TaxReportView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['tax-report', taxYear],
    queryFn: async () => {
      const [cashoutRequests, users, thresholds] = await Promise.all([
        dataProvider.getList('cashoutRequests', ALL),
        dataProvider.getList('users', ALL),
        dataProvider.getList('complianceThresholds', ALL),
      ]);
      const configThresholds = thresholdMapFromRows(
        thresholds.data as unknown as ComplianceThreshold[],
      );
      return buildTaxReportView(
        {
          cashoutRequests: cashoutRequests.data as unknown as CashoutRequest[],
          users: users.data as unknown as User[],
        },
        {
          taxYear,
          // Config wins per year; the statutory constant fills any year the config
          // omits, so an absent config still renders the statutory figure, never blank.
          thresholds: { ...THRESHOLD_BY_YEAR, ...configThresholds },
        },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
